/**
 * Framework-agnostic HTTP transport for the Hotline backend.
 *
 * Responsibilities:
 *  - Prefix requests with the configured `baseUrl` (e.g. `https://api.hotline.red`).
 *  - Attach `Authorization: Bearer <accessToken>` from the injected `TokenStore`.
 *  - On a 401, transparently call `POST /auth/refresh` once with the stored
 *    refresh token, persist the rotated pair, and retry the original request.
 *    Concurrent 401s share a single in-flight refresh.
 *  - Surface non-2xx responses — and 2xx responses whose body is not the JSON
 *    the caller was typed to expect — as a typed `ApiError` (status + body).
 *
 * Pure transport — no React, no DOM. The REST API client and the realtime client
 * are layered on top. `fetch` is injectable for tests.
 */
import type { TokenPair } from '@hotline/contracts';

import type { TokenStore } from './types.js';

/** The subset of `fetch` we depend on (so tests can inject a stub). */
export type FetchLike = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
  },
) => Promise<HttpResponseLike>;

/** Minimal `Response`-like surface. */
export interface HttpResponseLike {
  readonly ok: boolean;
  readonly status: number;
  text(): Promise<string>;
}

/**
 * Thrown for any response the caller cannot use: a non-2xx (after a refresh
 * retry, if applicable), or a 2xx whose body was supposed to be JSON and wasn't.
 * `body` carries whatever the server actually sent — parsed when it was JSON,
 * raw text when it wasn't — so a proxy's error page survives for diagnosis.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface HttpClientOptions {
  /** Backend base URL, e.g. `https://api.hotline.red`. Trailing slash trimmed. */
  baseUrl: string;
  /** Token persistence. Defaults to an in-memory store if omitted. */
  tokenStore: TokenStore;
  /** Injected fetch (defaults to global `fetch`). */
  fetch?: FetchLike;
  /** Called whenever tokens are cleared due to an unrecoverable 401. */
  onAuthError?: () => void;
}

interface RequestOptions {
  method?: string;
  /** JSON body (serialized automatically). */
  body?: unknown;
  /** Query params (undefined values dropped). */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Whether to attach the bearer token (default true). */
  auth?: boolean;
  signal?: AbortSignal;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly tokenStore: TokenStore;
  private readonly doFetch: FetchLike;
  private readonly onAuthError?: () => void;
  /** Shared in-flight refresh so concurrent 401s don't stampede. */
  private refreshInFlight: Promise<TokenPair | null> | null = null;

  constructor(options: HttpClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.tokenStore = options.tokenStore;
    this.doFetch =
      options.fetch ??
      ((input, init) =>
        (globalThis.fetch as unknown as FetchLike)(input, init));
    this.onAuthError = options.onAuthError;
  }

  get tokens(): TokenPair | null {
    return this.tokenStore.get();
  }

  setTokens(tokens: TokenPair | null): void {
    this.tokenStore.set(tokens);
  }

  /** Build a full URL with query string. */
  private url(
    path: string,
    query?: RequestOptions['query'],
  ): string {
    const base = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query) return base;
    const parts: string[] = [];
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
    return parts.length ? `${base}?${parts.join('&')}` : base;
  }

  /** Run a single fetch with the current access token attached (if requested). */
  private async raw(
    path: string,
    options: RequestOptions,
  ): Promise<HttpResponseLike> {
    const headers: Record<string, string> = {};
    let body: string | undefined;
    if (options.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(options.body);
    }
    if (options.auth !== false) {
      const access = this.tokenStore.get()?.accessToken;
      if (access) headers['authorization'] = `Bearer ${access}`;
    }
    return this.doFetch(this.url(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal,
    });
  }

  /**
   * Perform a request and decode JSON. On a 401 (when authed), attempts exactly
   * one refresh+retry before throwing `ApiError`.
   */
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let res = await this.raw(path, options);

    if (res.status === 401 && options.auth !== false) {
      const refreshed = await this.refreshTokens();
      if (refreshed) {
        res = await this.raw(path, options);
      }
    }

    return this.decode<T>(res);
  }

  /**
   * Decode a response body, throwing `ApiError` for non-2xx.
   *
   * A 2xx body that is present but not JSON is also an error: it means something
   * other than the API answered (a captive portal, a proxy's HTML error page, an
   * SPA index.html from a misrouted path). Returning the raw text cast to `T`
   * would hand callers a string wearing a domain type's clothes, and the failure
   * would surface much later as an unrelated property access on undefined.
   * An empty 2xx body stays legal — that is how the write routes acknowledge.
   */
  private async decode<T>(res: HttpResponseLike): Promise<T> {
    const text = await res.text();
    const parsed = text.length ? parseJson(text) : undefined;

    if (!res.ok) {
      // Keep whatever the server sent, JSON or not — support reads these.
      const body = parsed === INVALID_JSON ? text : parsed;
      const message = messageOf(body) || `Request failed with status ${res.status}`;
      throw new ApiError(res.status, message, body ?? text);
    }

    if (parsed === INVALID_JSON) {
      throw new ApiError(
        res.status,
        `Expected a JSON response body but got ${describeBody(text)}`,
        text,
      );
    }
    return parsed as T;
  }

  /**
   * Refresh the access token using the stored refresh token. Idempotent across
   * concurrent callers (single shared promise). Clears tokens + fires
   * `onAuthError` if refresh fails.
   */
  async refreshTokens(): Promise<TokenPair | null> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const current = this.tokenStore.get();
    if (!current?.refreshToken) {
      this.handleAuthError();
      return null;
    }

    this.refreshInFlight = (async () => {
      try {
        const res = await this.raw('/auth/refresh', {
          method: 'POST',
          body: { refreshToken: current.refreshToken },
          auth: false,
        });
        if (!res.ok) {
          this.handleAuthError();
          return null;
        }
        const text = await res.text();
        const parsed = parseJson(text);
        const next = parsed === INVALID_JSON ? null : (parsed as TokenPair | null);
        if (!next?.accessToken) {
          this.handleAuthError();
          return null;
        }
        this.tokenStore.set(next);
        return next;
      } catch {
        this.handleAuthError();
        return null;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private handleAuthError(): void {
    this.tokenStore.clear();
    this.onAuthError?.();
  }
}

/**
 * Returned by `parseJson` when the body is not JSON. A sentinel rather than the
 * raw text, so callers can tell "the server sent the string `x`" apart from "the
 * server sent something that is not JSON at all" — the distinction the 2xx path
 * depends on.
 */
const INVALID_JSON = Symbol('invalid-json');

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return INVALID_JSON;
  }
}

/** Pull a string `message` off a parsed error body, if it has one. */
function messageOf(body: unknown): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const { message } = body as { message: unknown };
    if (typeof message === 'string') return message;
  }
  return '';
}

/**
 * A short characterisation of a non-JSON body for the error message. The full
 * text is preserved on `ApiError.body`, so this only has to be enough to tell an
 * intercepting proxy's HTML page from other junk at a glance in a log or toast.
 */
function describeBody(text: string): string {
  return text.trimStart().startsWith('<') ? 'an HTML document' : 'a non-JSON body';
}
